import React, { memo, useCallback } from 'react';
import { Plus, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { useStrengths } from '../../../../hooks/useStrengths';
import { useFormContext } from 'react-hook-form';

// Memoized strength input component to prevent unnecessary re-renders
const StrengthInput = memo(({ 
  strength, 
  index, 
  onChange, 
  onMoveUp, 
  onMoveDown, 
  onRemove,
  isFirst,
  isLast 
}: { 
  strength: { id: string, description: string }; 
  index: number; 
  onChange: (index: number, value: string) => void; 
  onMoveUp: (index: number) => void; 
  onMoveDown: (index: number) => void;
  onRemove: (index: number) => void;
  isFirst: boolean;
  isLast: boolean;
}) => {
  // Prevent immediate updates that cause focus loss
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, e.target.value);
  };

  return (
    <div className="flex items-start gap-4">
      <div className="flex-grow">
        <input
          type="text"
          value={strength.description}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
          placeholder="Es. Ottima esposizione, Recentemente ristrutturato..."
        />
      </div>
      <div className="flex items-center space-x-2">
        <button
          type="button"
          onClick={() => onMoveUp(index)}
          disabled={isFirst}
          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onMoveDown(index)}
          disabled={isLast}
          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-1 text-red-500 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});

const StrengthsSection = () => {
  useFormContext(); // Ensure we're inside a form context
  const {
    strengths,
    handleAddStrength,
    handleRemoveStrength,
    handleStrengthChange,
    handleMoveStrength
  } = useStrengths();

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-4 pb-2 border-b">
        <h3 className="text-lg font-semibold">Punti di Forza</h3>
        <button
          type="button"
          onClick={handleAddStrength}
          className="text-primary hover:text-primary-dark flex items-center"
        >
          <Plus className="h-4 w-4 mr-1" />
          Aggiungi
        </button>
      </div>
      
      <div className="space-y-4">
        {strengths.map((strength, index) => (
          <StrengthInput
            key={strength.id}
            strength={strength}
            index={index}
            onChange={handleStrengthChange}
            onMoveUp={() => handleMoveStrength(index, 'up')}
            onMoveDown={() => handleMoveStrength(index, 'down')}
            onRemove={handleRemoveStrength}
            isFirst={index === 0}
            isLast={index === strengths.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(StrengthsSection);